import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { validateInvitation, acceptInvitation } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';

export const AcceptInvitationPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    const { login } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [validating, setValidating] = useState(true);
    const [invitation, setInvitation] = useState(null);
    const [error, setError] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (token) {
            validateToken();
        } else {
            setError('Kein Einladungs-Token vorhanden');
            setLoading(false);
            setValidating(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const validateToken = async () => {
        try {
            const res = await validateInvitation(token);
            setInvitation(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Einladung ungültig oder abgelaufen');
        } finally {
            setLoading(false);
            setValidating(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 6) {
            toast.error('Passwort muss mindestens 6 Zeichen haben');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        
        setSubmitting(true);
        try {
            const res = await acceptInvitation({ token, password });
            toast.success('Konto erfolgreich erstellt!');
            login(res.data.token, res.data.user);
            navigate('/my-orders');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Erstellen des Kontos');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || validating) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                    <p className="mt-4 text-muted-foreground">Einladung wird überprüft...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                            <h2 className="text-xl font-bold mb-2">Einladung ungültig</h2>
                            <p className="text-muted-foreground mb-6">{error}</p>
                            <Button onClick={() => navigate('/login')}>
                                Zur Anmeldung
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="accept-invitation-page">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle2 className="h-8 w-8 text-emerald-600" />
                    </div>
                    <CardTitle className="text-2xl">Willkommen, {invitation?.name}!</CardTitle>
                    <CardDescription>
                        Legen Sie ein Passwort fest, um Ihr Konto zu aktivieren
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label>E-Mail</Label>
                            <Input
                                type="email"
                                value={invitation?.email || ''}
                                disabled
                                className="bg-slate-50"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Passwort</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Mindestens 6 Zeichen"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    data-testid="password-input"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                            <Input
                                id="confirmPassword"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Passwort wiederholen"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>
                        <Button 
                            type="submit" 
                            className="w-full"
                            disabled={submitting}
                            data-testid="activate-account-btn"
                        >
                            {submitting ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : null}
                            Konto aktivieren
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default AcceptInvitationPage;
